import { Col, DatePicker, Form, Input, message, Modal, Row } from "antd";
import { Rule } from "antd/lib/form";
import { customerApi } from "api/customer.api";
import { feedbackApi } from "api/feedback.api";
import { AppButton, AppSelect } from "components/antd-ui";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Customer } from "types/customer";
import { Feedback, FeedbackStatus } from "types/feedback";
import { ModalStatus } from "types/modal";
import { can } from "utils/auth";
import { formatDateTime } from "utils/date";
import dayjs, { Dayjs } from "utils/dayjs";
import { FEEDBACK_PATH, feedbackStatusOptions, staffName } from "../feedback.meta";

const requiredRules: Rule[] = [{ required: true, message: "Bắt buộc" }];

type CustomerOption = { label: string; value: number };

interface FeedbackForm {
  content: string;
  feedbackAt?: Dayjs;
  note?: string;
  status: FeedbackStatus;
  customerId?: number;
}

export interface FeedbackModal {
  handleCreate: () => void;
  handleUpdate: (feedback: Feedback) => void;
  handleView: (feedback: Feedback) => void;
}

interface FeedbackModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

const toForm = (feedback: Feedback): FeedbackForm => ({
  content: feedback.content || "",
  feedbackAt: feedback.feedbackAt > 0 ? dayjs.unix(feedback.feedbackAt) : undefined,
  note: feedback.note || "",
  status: feedback.status,
  customerId: feedback.customer?.id,
});

const FeedbackDetail = ({ feedback }: { feedback: Feedback }) => {
  const rows = [
    { label: "Khách hàng", value: feedback.customer?.name || "--" },
    { label: "Nội dung", value: feedback.content || "--" },
    { label: "Thời gian phản hồi", value: feedback.feedbackAt > 0 ? formatDateTime(feedback.feedbackAt) : "--" },
    { label: "Ghi chú nội bộ", value: feedback.note || "--" },
    { label: "Trạng thái", value: feedbackStatusOptions.find((option) => option.value === feedback.status)?.label || "--" },
    { label: "Người tạo", value: staffName(feedback.createdStaff) },
    { label: "Người xử lý", value: staffName(feedback.handledStaff) },
    { label: "Giờ hoàn thành", value: formatDateTime(feedback.completedAt) },
  ];

  return (
    <div>
      <div className="feedbackModal__title">Thông tin phản hồi</div>
      <div className="feedbackModal__grid">
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <span className="feedbackModal__label">{row.label}</span>
            <span>{row.value}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export const FeedbackModal = observer(
  React.forwardRef(({ onClose, onSubmitOk }: FeedbackModalProps, ref) => {
    const [form] = Form.useForm<FeedbackForm>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [status, setStatus] = useState<ModalStatus>("create");
    const [selectedFeedback, setSelectedFeedback] = useState<Feedback>();
    const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);

    const canUpdate = can(FEEDBACK_PATH, "UPDATE");

    const fetchCustomer = useCallback(
      async (keyword: string) => {
        const res = await customerApi.findAll({ page: 1, limit: 20, search: keyword });
        const customers: Customer[] = res.data.customers || [];
        setCustomerOptions((prev) => {
          const selectedId = form.getFieldValue("customerId");
          const selected = prev.find((option) => option.value === selectedId);
          const options = customers.map((customer) => ({
            label: customer.name,
            value: customer.id,
          }));
          if (selected && !options.some((option) => option.value === selected.value)) {
            return [selected, ...options];
          }
          return options;
        });
      },
      [form],
    );

    const debounceSearchCustomer = useMemo(
      () =>
        debounce((keyword: string) => {
          fetchCustomer(keyword);
        }, 500),
      [fetchCustomer],
    );

    useEffect(() => {
      return () => {
        debounceSearchCustomer.cancel();
      };
    }, [debounceSearchCustomer]);

    useEffect(() => {
      if (!visible || status === "view") {
        return;
      }
      fetchCustomer("");
    }, [visible, status, fetchCustomer]);

    const fillFeedback = useCallback(
      (feedback: Feedback) => {
        form.setFieldsValue(toForm(feedback));
        if (feedback.customer) {
          setCustomerOptions([
            {
              label: feedback.customer.name,
              value: feedback.customer.id,
            },
          ]);
        } else {
          setCustomerOptions([]);
        }
        setSelectedFeedback(feedback);
      },
      [form],
    );

    useImperativeHandle<any, FeedbackModal>(
      ref,
      () => ({
        handleCreate() {
          form.resetFields();
          form.setFieldsValue({ status: "PENDING" });
          setCustomerOptions([]);
          setSelectedFeedback(undefined);
          setStatus("create");
          setVisible(true);
        },
        handleUpdate(feedback: Feedback) {
          fillFeedback(feedback);
          setStatus("update");
          setVisible(true);
        },
        handleView(feedback: Feedback) {
          fillFeedback(feedback);
          setStatus("view");
          setVisible(true);
        },
      }),
      [fillFeedback, form],
    );

    const handleClose = useCallback(() => {
      setVisible(false);
      setSelectedFeedback(undefined);
      onClose();
    }, [onClose]);

    const handleStartEdit = useCallback(() => {
      setStatus("update");
    }, []);

    const buildPayload = useCallback(() => {
      const values = form.getFieldsValue();
      return {
        feedback: {
          content: (values.content || "").trim(),
          feedbackAt: values.feedbackAt ? values.feedbackAt.unix() : 0,
          note: values.note || "",
          status: values.status || "PENDING",
        },
        customerId: values.customerId,
      };
    }, [form]);

    const handleSubmit = useCallback(async () => {
      await form.validateFields();
      const data = buildPayload();
      setLoading(true);
      try {
        if (status === "create") {
          await feedbackApi.create(data);
        } else {
          await feedbackApi.update(selectedFeedback?.id || 0, data);
        }
        message.success(status === "create" ? "Tạo mới thành công!" : "Cập nhật thành công!");
        handleClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    }, [buildPayload, form, handleClose, onSubmitOk, selectedFeedback?.id, status]);

    const title =
      status === "create" ? "Tạo phản hồi" : status === "update" ? "Cập nhật phản hồi" : "Xem phản hồi";

    return (
      <Modal
        className="feedbackModal"
        onCancel={handleClose}
        open={visible}
        title={title}
        style={{ top: 20 }}
        width={720}
        confirmLoading={loading}
        footer={
          status === "view" ? (
            <div className="feedbackModal__footer">
              <AppButton onClick={handleClose}>Đóng</AppButton>
              {canUpdate && (
                <AppButton tone="primary" onClick={handleStartEdit}>
                  Sửa
                </AppButton>
              )}
            </div>
          ) : (
            <div className="feedbackModal__footer">
              <AppButton onClick={handleClose}>Hủy</AppButton>
              <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
                Lưu
              </AppButton>
            </div>
          )
        }
      >
        {status === "view" && selectedFeedback ? (
          <FeedbackDetail feedback={selectedFeedback} />
        ) : (
          <Form layout="vertical" form={form}>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Khách hàng" name="customerId" rules={requiredRules}>
                  <AppSelect
                    showSearch
                    filterOption={false}
                    options={customerOptions}
                    onSearch={debounceSearchCustomer}
                    placeholder="Chọn khách hàng"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Nội dung phản hồi" name="content" rules={requiredRules}>
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Thời gian phản hồi" name="feedbackAt" rules={requiredRules}>
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY HH:mm"
                    placeholder="Chọn thời gian"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Trạng thái" name="status" rules={requiredRules}>
                  <AppSelect options={feedbackStatusOptions} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Ghi chú nội bộ" name="note">
                  <Input.TextArea rows={3} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>
    );
  }),
);
